import api from '../config/api';
import { API_ENDPOINTS } from '../config/constants';

export const productService = {
  // Get all products with filters and pagination (public / customer-facing)
  getAll: async (filters = {}, pagination = {}) => {
    const { page = 1, limit = 20, sort = 'created_at', order = 'desc' } = pagination;
    const params = { page, limit, sort, order, ...filters };
    const response = await api.get(API_ENDPOINTS.PRODUCTS.LIST, { params });
    return response.data;
  },

  // Get single product by ID
  getById: async (id) => {
    const response = await api.get(API_ENDPOINTS.PRODUCTS.GET(id));
    return response.data;
  },

  // Get product by slug
  getBySlug: async (slug) => {
    const response = await api.get(API_ENDPOINTS.PRODUCTS.BY_SLUG(slug));
    return response.data;
  },

  // Seller-scoped — returns only own products + server-computed accurate stats
  getMyProducts: async (params = {}) => {
    const response = await api.get(API_ENDPOINTS.SELLER.MY_PRODUCTS, { params });
    return response.data;
  },

  // Create product (seller only)
  create: async (productData, images = []) => {
    const formData = new FormData();

    Object.keys(productData).forEach(key => {
      // Skip null, undefined, and empty strings except for arrays which might be empty
      if (
        productData[key] !== null && 
        productData[key] !== undefined && 
        productData[key] !== ''
      ) {
        if (Array.isArray(productData[key])) {
          formData.append(key, JSON.stringify(productData[key]));
        } else {
          formData.append(key, productData[key]);
        }
      }
    });

    images.forEach(image => formData.append('images', image));

    const response = await api.post(API_ENDPOINTS.PRODUCTS.CREATE, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Update product
  update: async (id, productData, newImages = []) => {
    const formData = new FormData();

    Object.keys(productData).forEach(key => {
      // Skip null, undefined, and empty strings except for arrays which might be empty
      if (
        productData[key] !== null && 
        productData[key] !== undefined && 
        productData[key] !== ''
      ) {
        if (Array.isArray(productData[key])) {
          formData.append(key, JSON.stringify(productData[key]));
        } else {
          formData.append(key, productData[key]);
        }
      }
    });

    newImages.forEach(image => formData.append('images', image));

    const response = await api.put(API_ENDPOINTS.PRODUCTS.UPDATE(id), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Delete product
  delete: async (id) => {
    const response = await api.delete(API_ENDPOINTS.PRODUCTS.DELETE(id));
    return response.data;
  },

  // Update inventory
  updateInventory: async (id, quantity) => {
    const response = await api.patch(`${API_ENDPOINTS.PRODUCTS.GET(id)}/inventory`, { quantity });
    return response.data;
  },

  // Check stock availability
  checkStock: async (id, quantity) => {
    const response = await api.get(`${API_ENDPOINTS.PRODUCTS.GET(id)}/stock`, {
      params: { quantity },
    });
    return response.data;
  },

  // Get products by category
  getByCategory: async (categoryId, pagination = {}) => {
    return await productService.getAll({ category_id: categoryId }, pagination);
  },

  // Get products by seller
  getBySeller: async (sellerId, pagination = {}) => {
    return await productService.getAll({ seller_id: sellerId }, pagination);
  },

  // Search products
  search: async (query, filters = {}, pagination = {}) => {
    return await productService.getAll({ search: query, ...filters }, pagination);
  },
};

export default productService;